import io from 'socket.io-client'
import { debounce } from 'lodash'

const logEvent = (message, data) => {
    if (window.location.href.includes('debug')) {
        console.log(message, data)
    }
}

const BATCH_SIZE = 10
const DEBOUNCE_WAIT = 200
const MAX_WAIT = 2000

let socket = null

// queues (global like your mixin instance state)
let baseQueue = Object.create(null)
let sharingQueue = Object.create(null)

let storeRef = null

let flushQueuesDebounced = null

// Tokens are replayed whenever Socket.IO reconnects.
let authTokens = Object.create(null)

// Track connection gaps until a later resync succeeds.
let pendingResync = false
let gapId = 0

let isResyncing = false
let failedAttempts = 0
let lastResyncAt = 0
let resyncTimer = null

const RESYNC_MIN_INTERVAL = 5000
const RESYNC_MAX_ATTEMPTS = 3

function markGap() {
    pendingResync = true
    gapId += 1
    failedAttempts = 0
}

function scheduleResync(delay) {
    if (resyncTimer) return

    resyncTimer = setTimeout(() => {
        resyncTimer = null
        resync()
    }, delay)
}

function flushQueues() {
    if (!storeRef?.state?.tracker?.shouldUpdateTrackers) return

    const baseItems = Object.values(baseQueue)
    const sharingItems = Object.values(sharingQueue)

    baseQueue = Object.create(null)
    sharingQueue = Object.create(null)

    if (baseItems.length) {
        logEvent('Updating items', baseItems)
        storeRef.dispatch('tracker/batchUpdateTrackerLive', baseItems)
    }

    if (sharingItems.length) {
        logEvent('Updating shared items', sharingItems)
        storeRef.dispatch('sharing/batchUpdateSharedTrackerLive', sharingItems)
    }
}

function ensureDebounce() {
    if (!flushQueuesDebounced) {
        flushQueuesDebounced = debounce(flushQueues, DEBOUNCE_WAIT, {
            maxWait: MAX_WAIT,
        })
    }
}

function enqueue(type, data) {
    logEvent('Received asset update', data)

    const queue = type === 'base' ? baseQueue : sharingQueue
    queue[data.tracker] = data

    if (
        storeRef?.state?.tracker?.shouldUpdateTrackers &&
        Object.values(queue).length > BATCH_SIZE
    ) {
        flushQueuesDebounced?.cancel()
        flushQueues()
        return
    }

    flushQueuesDebounced()
}

function emitAuthentication(target) {
    Object.values(authTokens).forEach(token => {
        if (token) {
            target.emit('authenticate', { token })
        }
    })
}

// Refetch snapshots to repair missed live updates.
function resync() {
    if (!storeRef || !pendingResync) return

    // Wait for a live connection before retrying.
    if (!socket?.connected) return

    const isSharing = Boolean(storeRef.state.sharing?.sharingConfig)

    if (!isSharing) {
        if (!storeRef.state.auth?.jwt) return

        // Avoid a resync that would log out an expired session.
        if (!storeRef.getters['auth/isSessionRefreshable']) {
            logEvent('Skipping resync, session can no longer be refreshed')
            return
        }
    }

    if (isResyncing) return

    // Avoid repeated refetches while the connection flaps.
    const elapsed = Date.now() - lastResyncAt

    if (elapsed < RESYNC_MIN_INTERVAL) {
        scheduleResync(RESYNC_MIN_INTERVAL - elapsed)
        return
    }

    const resyncedGapId = gapId

    isResyncing = true
    lastResyncAt = Date.now()

    storeRef
        .dispatch(
            isSharing
                ? 'sharing/resyncSharedTrackers'
                : 'tracker/resyncTrackers'
        )
        .then(() => {
            // Keep gaps that opened during this request pending.
            if (gapId === resyncedGapId) {
                pendingResync = false
            }

            failedAttempts = 0
        })
        .catch(error => {
            failedAttempts += 1
            logEvent('Resync failed', error)
        })
        .finally(() => {
            isResyncing = false

            // Retry a limited number of times without hammering the API.
            if (pendingResync && failedAttempts < RESYNC_MAX_ATTEMPTS) {
                scheduleResync(RESYNC_MIN_INTERVAL)
            }
        })
}

function setupListeners() {
    if (!socket) return

    // Ignore events from sockets replaced by a later connection.
    const activeSocket = socket

    activeSocket.on('connect', () => {
        if (socket !== activeSocket) return

        logEvent('Socket connected', activeSocket.id)

        // Re-authenticate before fetching missed updates.
        emitAuthentication(activeSocket)

        resync()
    })

    activeSocket.on('disconnect', reason => {
        if (socket !== activeSocket) return

        logEvent('Socket disconnected', reason)

        markGap()
    })

    activeSocket.on('asset_update', data => {
        if (socket !== activeSocket) return

        if (storeRef?.state?.sharing?.sharingConfig) {
            enqueue('sharing', data) // live-update for share tokens
        } else {
            enqueue('base', data) // live-update for regular login
        }
    })

    activeSocket.on('debug', data => {
        if (socket !== activeSocket) return

        if (window.location.href.includes('debug')) {
            console.log('Socket debug:', data)
        }

        if (data === 'Invalid token') {
            storeRef?.dispatch('auth/refreshTokenIfNecessary')
        }
    })
}

export function connect(url, store) {
    storeRef = store

    if (socket) {
        // Prevent the replaced socket from triggering a resync.
        const previousSocket = socket

        socket = null
        previousSocket.disconnect()
    }

    socket = io(url, {
        transports: ['websocket'],
    })

    ensureDebounce()
    setupListeners()

    return socket
}

export function disconnect() {
    // Let the active socket record this deliberate gap.
    socket?.disconnect()
    socket = null

    baseQueue = Object.create(null)
    sharingQueue = Object.create(null)

    flushQueuesDebounced?.cancel()
    flushQueuesDebounced = null

    clearTimeout(resyncTimer)
    resyncTimer = null

    authTokens = Object.create(null)
}

export function authenticate(token, kind = 'base') {
    authTokens[kind] = token

    // Reconnect will replay tokens stored while offline.
    if (socket?.connected) {
        socket.emit('authenticate', { token })
    }
}

export function deauthenticate(kind) {
    delete authTokens[kind]
}

// used by plugin if needed
export function getSocket() {
    return socket
}
